import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, Wifi, WifiOff, CheckCircle2, XCircle, LogIn, LogOut, Search, X, UserPlus, Clock, Users, ArrowLeft, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import nfcService, { NFCPunchResponse, NFCTodayLog, NFCTodaySummary, NFCSearchEmployee } from '../services/nfcService';

// Web NFC API type declarations
declare global {
  interface Window {
    NDEFReader: any;
  }
}

type ScanState = 'idle' | 'scanning' | 'success_in' | 'success_out' | 'unregistered' | 'error' | 'unsupported';

const NFCAttendance: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAppContext();
  const [scanState, setScanState] = useState<ScanState>('idle');
  const [lastPunch, setLastPunch] = useState<NFCPunchResponse | null>(null);
  const [lastCardUid, setLastCardUid] = useState<string>('');
  const [summary, setSummary] = useState<NFCTodaySummary>({ totalIn: 0, totalOut: 0, uniqueEmployees: 0, date: '' });
  const [todayLogs, setTodayLogs] = useState<NFCTodayLog[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [readErrorCount, setReadErrorCount] = useState(0);

  // Kart kayit modal
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<NFCSearchEmployee[]>([]);
  const [searching, setSearching] = useState(false);
  const [registering, setRegistering] = useState(false);

  const ndefReaderRef = useRef<any>(null);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Saat guncelleme
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Ozet ve loglari yukle
  const loadData = useCallback(async () => {
    try {
      const [summaryRes, logsRes] = await Promise.all([
        nfcService.getTodaySummary(),
        nfcService.getTodayLogs()
      ]);
      if (summaryRes.success) setSummary(summaryRes.data);
      if (logsRes.success) setTodayLogs(logsRes.data);
    } catch (err) {
      console.error('Data load error:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
    const refreshInterval = setInterval(loadData, 30000);
    return () => clearInterval(refreshInterval);
  }, [loadData]);

  // NFC temizleme
  useEffect(() => {
    // NFC destegi kontrol - sadece bilgilendirme
    if (!('NDEFReader' in window)) {
      setScanState('unsupported');
      setErrorMessage(t('nfcAttendance.nfcBrowserNotSupported'));
    }
    return () => {
      if (ndefReaderRef.current) {
        try { ndefReaderRef.current.onreading = null; } catch {}
      }
    };
  }, []);

  // NFC baslatma - MUTLAKA buton tiklamasi ile cagirilmali (user gesture)
  const startNFCScan = async () => {
    if (!('NDEFReader' in window)) {
      setScanState('unsupported');
      setErrorMessage(t('nfcAttendance.nfcBrowserNotSupported'));
      return;
    }

    setScanState('scanning');
    setErrorMessage('');
    setReadErrorCount(0);

    try {
      const ndef = new window.NDEFReader();
      await ndef.scan();
      ndefReaderRef.current = ndef;

      ndef.onreading = async (event: any) => {
        const serialNumber = event.serialNumber;
        console.log('NFC onreading - serialNumber:', serialNumber);
        console.log('NFC onreading - message records:', event.message?.records?.length);

        // Basarili okuma - hata sayacini sifirla
        setReadErrorCount(0);

        let cardUid = '';

        // 1. serialNumber varsa kullan (en guvenilir)
        if (serialNumber && serialNumber !== '' && serialNumber !== 'undefined' && serialNumber !== 'null') {
          cardUid = serialNumber.replace(/:/g, '').toUpperCase();
        }

        // 2. serialNumber yoksa message records'dan UID almaya calis
        if (!cardUid && event.message && event.message.records) {
          for (const record of event.message.records) {
            try {
              if (record.data) {
                // ArrayBuffer'dan hex string'e cevir
                const bytes = new Uint8Array(record.data.buffer || record.data);
                const hexUid = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
                if (hexUid.length >= 4) {
                  cardUid = hexUid;
                  break;
                }
                // Text olarak da dene
                const decoder = new TextDecoder();
                const textUid = decoder.decode(record.data);
                const cleanUid = textUid.replace(/[^a-fA-F0-9]/g, '');
                if (cleanUid.length >= 4) {
                  cardUid = cleanUid.toUpperCase();
                  break;
                }
              }
            } catch (e) {
              console.warn('Record parse error:', e);
            }
          }
        }

        if (!cardUid) {
          setScanState('error');
          setErrorMessage(t('nfcAttendance.cardDetectedNoUid'));
          autoReset();
          return;
        }

        console.log('NFC Card UID:', cardUid);
        setLastCardUid(cardUid);
        await handlePunch(cardUid);
      };

      ndef.onreadingerror = (event: any) => {
        console.warn('NFC onreadingerror fired', event);
        setReadErrorCount(prev => {
          const newCount = prev + 1;
          if (newCount >= 3) {
            setScanState('error');
            setErrorMessage(t('nfcAttendance.cardTypeNotSupported'));
          } else {
            setScanState('error');
            setErrorMessage(t('nfcAttendance.cardReadError', { count: newCount }));
          }
          return newCount;
        });
        autoReset(6000);
      };

    } catch (err: any) {
      console.error('NFC scan error:', err.name, err.message);
      if (err.name === 'NotAllowedError') {
        setErrorMessage(t('nfcAttendance.nfcPermissionDenied'));
        setScanState('idle');
      } else if (err.name === 'NotSupportedError') {
        setErrorMessage(t('nfcAttendance.nfcNotAvailable'));
        setScanState('unsupported');
      } else if (err.name === 'AbortError') {
        setErrorMessage(t('nfcAttendance.nfcConnectionLost'));
        setScanState('idle');
      } else {
        setErrorMessage(`${t('nfcAttendance.nfcError')}: ${err.message}`);
        setScanState('idle');
      }
    }
  };

  const handlePunch = async (cardUid: string) => {
    try {
      const res = await nfcService.punch(cardUid);

      if (res.success && res.data && 'actionType' in res.data) {
        const punch = res.data as NFCPunchResponse;
        setLastPunch(punch);
        setScanState(punch.actionType === 'GIRIS' ? 'success_in' : 'success_out');
        loadData();
      } else {
        setScanState('unregistered');
      }
    } catch (err) {
      setScanState('error');
      setErrorMessage(t('nfcAttendance.serverError'));
    }

    autoReset();
  };

  const autoReset = (delay = 4000) => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      // Sadece NFC aktifse scanning'e don
      if (ndefReaderRef.current) {
        setScanState('scanning');
      } else {
        setScanState('idle');
      }
      setLastPunch(null);
      setErrorMessage('');
    }, delay);
  };

  // Personel arama (kart kayit icin)
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await nfcService.searchEmployee(searchQuery);
        if (res.success) setSearchResults(res.data);
      } catch {}
      setSearching(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleRegisterCard = async (employeeId: number) => {
    if (!lastCardUid) return;
    setRegistering(true);
    try {
      const res = await nfcService.registerCard(employeeId, lastCardUid);
      if (res.success) {
        setShowRegisterModal(false);
        setSearchQuery('');
        setSearchResults([]);
        await handlePunch(lastCardUid);
      }
    } catch (err) {
      console.error('Register error:', err);
    }
    setRegistering(false);
  };

  const formatTime = (date: Date) => date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const formatDate = (date: Date) => date.toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col select-none">
      {/* Header */}
      <header className="px-4 py-3 flex items-center justify-between bg-black/20 backdrop-blur-sm">
        <button onClick={() => navigate('/')} className="p-2 rounded-lg hover:bg-white/10 transition">
          <ArrowLeft size={20} />
        </button>
        <div className="text-center">
          <div className="text-xs text-slate-400 uppercase tracking-widest font-bold">{t('nfcAttendance.title')}</div>
          <div className="text-sm font-medium text-slate-300">{formatDate(currentTime)}</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-mono font-bold tabular-nums">{formatTime(currentTime)}</div>
        </div>
      </header>

      {/* NFC Scan Area */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-6">
        {/* Scan Status */}
        <div className={`w-48 h-48 rounded-full flex items-center justify-center transition-all duration-500 ${
          scanState === 'scanning' ? 'bg-blue-500/20 animate-pulse shadow-[0_0_60px_rgba(59,130,246,0.3)]' :
          scanState === 'success_in' ? 'bg-emerald-500/20 shadow-[0_0_60px_rgba(16,185,129,0.4)]' :
          scanState === 'success_out' ? 'bg-amber-500/20 shadow-[0_0_60px_rgba(245,158,11,0.4)]' :
          scanState === 'unregistered' ? 'bg-red-500/20 shadow-[0_0_60px_rgba(239,68,68,0.3)]' :
          scanState === 'error' ? 'bg-red-500/20' :
          'bg-slate-700/30'
        }`}>
          {scanState === 'scanning' && <Smartphone size={72} className="text-blue-400 animate-bounce" />}
          {scanState === 'success_in' && <LogIn size={72} className="text-emerald-400" />}
          {scanState === 'success_out' && <LogOut size={72} className="text-amber-400" />}
          {scanState === 'unregistered' && <XCircle size={72} className="text-red-400" />}
          {scanState === 'error' && <WifiOff size={72} className="text-red-400" />}
          {(scanState === 'idle' || scanState === 'unsupported') && <WifiOff size={72} className="text-slate-500" />}
        </div>

        {/* Status Text */}
        <div className="mt-6 text-center">
          {scanState === 'idle' && (
            <>
              <button
                onClick={startNFCScan}
                className="px-8 py-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-2xl text-lg font-bold flex items-center gap-3 mx-auto transition shadow-lg shadow-blue-600/30"
              >
                <Play size={24} /> {t('nfcAttendance.startNFCScan')}
              </button>
              <p className="text-sm text-slate-400 mt-3 max-w-xs mx-auto">
                {errorMessage || t('nfcAttendance.startHint')}
              </p>
            </>
          )}
          {scanState === 'scanning' && (
            <>
              <p className="text-xl font-bold text-blue-400">{t('nfcAttendance.nfcReady')}</p>
              <p className="text-sm text-slate-400 mt-1">{t('nfcAttendance.holdCardToPhone')}</p>
              <p className="text-xs text-slate-500 mt-2">{t('nfcAttendance.nfcAntennaHint')}</p>
            </>
          )}
          {scanState === 'success_in' && lastPunch && (
            <>
              <p className="text-2xl font-bold text-emerald-400">{t('nfcAttendance.entry')}</p>
              <p className="text-xl font-semibold text-white mt-2">{lastPunch.fullName}</p>
              <p className="text-sm text-slate-400 mt-1">{lastPunch.department}</p>
              <p className="text-lg font-mono text-emerald-300 mt-2">{lastPunch.time}</p>
            </>
          )}
          {scanState === 'success_out' && lastPunch && (
            <>
              <p className="text-2xl font-bold text-amber-400">{t('nfcAttendance.exit')}</p>
              <p className="text-xl font-semibold text-white mt-2">{lastPunch.fullName}</p>
              <p className="text-sm text-slate-400 mt-1">{lastPunch.department}</p>
              <p className="text-lg font-mono text-amber-300 mt-2">{lastPunch.time}</p>
            </>
          )}
          {scanState === 'unregistered' && (
            <>
              <p className="text-xl font-bold text-red-400">{t('nfcAttendance.unregisteredCard')}</p>
              <p className="text-xs text-slate-500 mt-1 font-mono">UID: {lastCardUid}</p>
              <button
                onClick={() => { setShowRegisterModal(true); setSearchQuery(''); setSearchResults([]); }}
                className="mt-4 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-xl text-sm font-bold flex items-center gap-2 mx-auto transition"
              >
                <UserPlus size={16} /> {t('nfcAttendance.assignToEmployee')}
              </button>
            </>
          )}
          {scanState === 'unsupported' && (
            <>
              <p className="text-xl font-bold text-red-400">{t('nfcAttendance.nfcNotSupported')}</p>
              <p className="text-sm text-slate-400 mt-1 max-w-xs mx-auto">{errorMessage || t('nfcAttendance.nfcNotSupportedDefault')}</p>
            </>
          )}
          {scanState === 'error' && (
            <>
              <p className="text-xl font-bold text-red-400">{t('nfcAttendance.error')}</p>
              <p className="text-sm text-slate-400 mt-1 max-w-xs mx-auto">{errorMessage}</p>
              <button
                onClick={startNFCScan}
                className="mt-4 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-xl text-sm font-bold flex items-center gap-2 mx-auto transition"
              >
                <Play size={16} /> {t('nfcAttendance.tryAgain')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Summary Bar */}
      <div className="px-4 py-3 bg-black/30 backdrop-blur-sm flex items-center justify-around border-t border-white/5">
        <div className="text-center">
          <div className="flex items-center gap-1.5 justify-center">
            <LogIn size={14} className="text-emerald-400" />
            <span className="text-2xl font-bold tabular-nums">{summary.totalIn}</span>
          </div>
          <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{t('nfcAttendance.checkIn')}</p>
        </div>
        <div className="w-px h-8 bg-slate-700" />
        <div className="text-center">
          <div className="flex items-center gap-1.5 justify-center">
            <LogOut size={14} className="text-amber-400" />
            <span className="text-2xl font-bold tabular-nums">{summary.totalOut}</span>
          </div>
          <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{t('nfcAttendance.checkOut')}</p>
        </div>
        <div className="w-px h-8 bg-slate-700" />
        <div className="text-center">
          <div className="flex items-center gap-1.5 justify-center">
            <Users size={14} className="text-blue-400" />
            <span className="text-2xl font-bold tabular-nums">{summary.uniqueEmployees}</span>
          </div>
          <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{t('nfcAttendance.personnel')}</p>
        </div>
      </div>

      {/* Today's Log List */}
      <div className="max-h-[35vh] overflow-y-auto bg-black/20">
        <div className="px-4 py-2 sticky top-0 bg-slate-900/90 backdrop-blur-sm border-b border-white/5">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{t('nfcAttendance.todayRecords')} ({todayLogs.length})</p>
        </div>
        {todayLogs.length === 0 ? (
          <div className="px-4 py-8 text-center text-slate-600 text-sm">{t('nfcAttendance.noRecordsYet')}</div>
        ) : (
          <div className="divide-y divide-white/5">
            {todayLogs.map((log) => (
              <div key={log.logId} className="px-4 py-2.5 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{log.fullName}</p>
                  <p className="text-[10px] text-slate-500">{log.department}</p>
                </div>
                <div className="flex items-center gap-3 text-xs ml-2 shrink-0">
                  {log.checkIn && (
                    <span className="flex items-center gap-1 text-emerald-400">
                      <LogIn size={12} /> {log.checkIn}
                    </span>
                  )}
                  {log.checkOut && (
                    <span className="flex items-center gap-1 text-amber-400">
                      <LogOut size={12} /> {log.checkOut}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Kart Kayit Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center">
          <div className="bg-slate-800 w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between shrink-0">
              <div>
                <p className="font-bold text-white">{t('nfcAttendance.assignCardToEmployee')}</p>
                <p className="text-[10px] text-slate-400 font-mono">UID: {lastCardUid}</p>
              </div>
              <button onClick={() => setShowRegisterModal(false)} className="p-2 rounded-lg hover:bg-white/10 transition">
                <X size={18} />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 py-3 border-b border-slate-700 shrink-0">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('nfcAttendance.searchEmployeePlaceholder')}
                  className="w-full pl-9 pr-4 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-white text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto">
              {searching && (
                <div className="px-4 py-8 text-center text-slate-400 text-sm">{t('nfcAttendance.searching')}</div>
              )}
              {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                <div className="px-4 py-8 text-center text-slate-500 text-sm">{t('nfcAttendance.noResults')}</div>
              )}
              {searchResults.map((emp) => (
                <button
                  key={emp.employeeId}
                  onClick={() => handleRegisterCard(emp.employeeId)}
                  disabled={registering || emp.hasCard}
                  className={`w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/50 transition border-b border-slate-700/30 ${
                    emp.hasCard ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <div className="text-left">
                    <p className="text-sm font-semibold text-white">{emp.fullName}</p>
                    <p className="text-[10px] text-slate-400">{emp.department} {emp.employeeCode ? `- ${emp.employeeCode}` : ''}</p>
                  </div>
                  {emp.hasCard ? (
                    <span className="text-[10px] text-amber-400 font-bold">{t('nfcAttendance.hasCard')}</span>
                  ) : (
                    <span className="text-[10px] text-blue-400 font-bold uppercase">{t('nfcAttendance.select')}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NFCAttendance;
